<template>
  <section class="infoUser">
    <div class="infoUser__wrapper" v-if="couldEditUser">
      <div class="infoUser__containerStatus">
        <div class="infoUser__headerStatus">
          <p class="infoUser__title">Estado de usuario</p>
          <div v-if="user.lastLogin" class="infoUser__sesionContainer">
            <span>{{ "Última sesión el " + lastLoginFormatted }}</span>
          </div>
        </div>
        <div class="infoUser__buttonsContainer">
          <span v-if="inactiveReasonLabel" class="infoUser__reasonBadge">
            {{ inactiveReasonLabel }}
          </span>
          <span class="infoUser__statusBadge" :class="check ? 'infoUser__statusBadge--active' : 'infoUser__statusBadge--inactive'">
            {{ check ? "Activo" : "Inactivo" }}
          </span>
          <button
            ref="menuTrigger"
            type="button"
            :class="`infoUser__iconsContainer ${showMenu ? 'infoUser__iconsContainer--active' : ''}`"
            aria-label="Abrir opciones de estado del usuario"
            :aria-expanded="showMenu"
            aria-haspopup="menu"
            @click.stop="toggleMenu"
            @keydown.esc="showMenu = false">
            <iconic name="verticalDots" class="infoUser__dots" aria-hidden="true" />
          </button>
          <transition name="slide-fade">
            <div v-if="showMenu" class="infoUser__menuOptions" ref="menuOptions" role="menu" aria-label="Opciones de estado">
              <button
                v-for="option in statusMenuOptions"
                :key="option.name"
                type="button"
                :class="`infoUser__option ${option.icon === 'changeInactivation' ? 'infoUser__option--changeReason' : ''}`"
                role="menuitem"
                @click="option.action">
                <iconic :name="option.icon" class="infoUser__optionIcon" aria-hidden="true" />
                <span>{{ option.name }}</span>
              </button>
            </div>
          </transition>
        </div>
      </div>
      <div class="infoUser__containerStatus" v-if="showStatusCard">
        <StatusHistoryCard
          :date="user.userStatusHistory.eventDate"
          :eventType="userStatusDic[user.userStatusHistory.eventType]"
          :user="user.userStatusHistory.performedByUser || user.userStatusHistory.performedBy"
          :type="user.userStatusHistory.eventType"
          :reason="user.userStatusHistory.reason"
          :description="user.userStatusHistory.description"
          :tags="user.userStatusHistory.tags"
          :role="user.role"
          :startDate="user.userStatusHistory.startDate"
          :endDate="user.userStatusHistory.endDate"
          :optionsDisabled="optionsDisabled"
          :canExpand="user.userStatusHistory.eventType !== 'enable'"
          :isExpanded="expandedCardId === `${user.id}-current`"
          @toggle="expandedCardId = expandedCardId === `${user.id}-current` ? null : `${user.id}-current`" />
      </div>
      <div class="infoUser__containerStatusFuture" v-if="showFutureCard">
        <StatusHistoryCard
          :date="user.futureUserStatus.eventDate"
          :eventType="userStatusDic[user.futureUserStatus.eventType]"
          :user="user.userStatusHistory.performedByUser || user.userStatusHistory.performedBy"
          :reason="user.futureUserStatus.reason"
          :description="user.futureUserStatus.description"
          :tags="user.futureUserStatus.tags"
          :role="user.role"
          :isFuture="true"
          :startDate="user.futureUserStatus.startDate"
          :endDate="user.futureUserStatus.endDate"
          :optionsDisabled="optionsDisabled"
          :canExpand="user.futureUserStatus.eventType !== 'enable'"
          :isExpanded="expandedCardId === `${user.id}-future`"
          @toggle="expandedCardId = expandedCardId === `${user.id}-future` ? null : `${user.id}-future`" />
      </div>
    </div>

    <div class="infoUser__wrapper">
      <div class="infoUser__header">
        <p class="infoUser__title">Información General</p>
        <a class="editUBtn" role="button" aria-label="Editar Usuario" v-if="couldEditUser" @click="openEditModal">
          <iconic class="infoUser__iconEdit" name="editNew" />
        </a>
      </div>
      <div class="infoUser__containerData">
        <div class="infoUser__containerInfo" v-for="(data, idx) in dataToShow" :key="idx">
          <span class="infoUser__infoName">{{ data.name }}</span>
          <span class="infoUser__infoValue">{{ data.value }}</span>
        </div>
      </div>
    </div>
    <StateUserModal
      v-if="showModalDisabled"
      mode="inactivacion"
      :isVisible="showModalDisabled"
      :user="user"
      :reset="reset"
      @closeModal="closeModal"
      @save="setUpdateUser"
      @refreshUser="handleRefreshUser" />
    <StateUserModal v-if="showModalLicense" mode="licencia" :isVisible="showModalLicense" :user="user" :reset="reset" @closeModal="closeModal" @save="setUpdateUser" @refreshUser="handleRefreshUser" />
    <StateUserModal v-if="showModalChangeReason" mode="changeReason" :isVisible="showModalChangeReason" :user="user" :reset="reset" @closeModal="closeModal" @refreshUser="handleRefreshUser" />
    <StateUserModal
      v-if="showModalChangeLicenseReason"
      mode="changeLicenseReason"
      :isVisible="showModalChangeLicenseReason"
      :user="user"
      :reset="reset"
      @closeModal="closeModal"
      @refreshUser="handleRefreshUser" />
    <StateUserModal
      v-if="showModalCancelLicense"
      mode="cancelLicense"
      :isVisible="showModalCancelLicense"
      :user="user"
      :reset="reset"
      @closeModal="closeModal"
      @save="setUpdateUser"
      @refreshUser="handleRefreshUser" />
    <ActiveUserModal v-if="showModalActive" :isVisible="showModalActive" :user="user" :reset="reset" @closeModal="closeModal" @save="setUpdateUser" @refreshUser="handleRefreshUser" />
    <SidebarUserStatus :showSidebar="showSidebar" :data="sidebarData" :optionsDisabled="optionsDisabled" title="Historial de cambios de estado" @closeSidebar="closeHistorySidebar" />
  </section>
</template>

<script>
  import { mapGetters, mapMutations } from "vuex";
  import Swal from "sweetalert2";
  import SidebarUserStatus from "../../../components/organisms/SidebarUserStatus.vue";
  import StatusHistoryCard from "../../../components/organisms/StatusHistoryCard.vue";
  import userStatusHistoryService from "../../../services/userStatusHistoryService";
  import userService from "../../../services/userService";
  import StateUserModal from "@/components/organisms/StateUserModal.vue";
  import IconLabel from "../../../components/molecules/IconLabel.vue";
  import ActiveUserModal from "@/components/organisms/ActiveUserModal.vue";

  export default {
    components: {
      ToggleInput: () => import("@/components/input/ToggleInput.vue"),
      StateUserModal: () => import("@/components/organisms/StateUserModal.vue"),
      SidebarUserStatus,
      StatusHistoryCard,
      IconLabel,
      ActiveUserModal,
    },
    data() {
      return {
        check: false,
        showModalDisabled: false,
        showModalActive: false,
        showModalLicense: false,
        showModalChangeReason: false,
        showModalChangeLicenseReason: false,
        showModalCancelLicense: false,
        reset: 1,
        dicKeys: {
          name: "Nombre completo",
          documentType: "Tipo de documento",
          documentId: "Doc. Identificación",
          email: "Correo",
          phone: "Celular",
          city: "Ciudad",
          office: "Oficina",
          workShift: "Turno",
          room: "Habitación",
          entrydate: "Fecha de ingreso",
          sessionLocalIPs: "Ips ultima sesión",
        },
        dicTurns: {
          morning: "Mañana",
          late: "tarde",
          night: "noche",
        },
        options: { month: "2-digit", day: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", hourCycle: "h12" },
        showSidebar: false,
        sidebarData: [],
        expandedCardId: null,
        showMenu: false,
        userStatusDic: {
          enable: "Activación",
          disable: "Inactivación",
          license: "Licencia",
          disableAutomated: "Inactivación",
        },
      };
    },
    computed: {
      ...mapGetters("applications", {
        getAppByName: "getAppByName",
      }),
      user() {
        return this.$store.state.user.userSelected;
      },
      lastLoginFormatted() {
        if (!this.user?.lastLogin) return "";
        return this.$global.dateLocaleFormat(new Date(this.user.lastLogin), {
          day: "numeric",
          month: "short",
          hour: "2-digit",
          minute: "2-digit",
          hourCycle: "h12",
        });
      },
      showStatusCard() {
        if (!this.user?.userStatusHistory) return false;

        return Object.values(this.user.userStatusHistory).some((value) => (Array.isArray(value) ? value.length > 0 : !!value));
      },
      showFutureCard() {
        if (!this.user?.futureUserStatus) return false;

        return Object.values(this.user.futureUserStatus).some((value) => (Array.isArray(value) ? value.length > 0 : !!value));
      },
      dataToShow() {
        const { user = {} } = this;
        const typesDocuments = this.$global.dictionary.documentType;
        const { name, documentType, documentId, email, phone, ...dicKeys } = this.dicKeys;
        const dataToShow = this.$userData.role !== "superadmin" ? dicKeys : this.dicKeys;
        const responseValues = {
          name: (user) => this.formatFullName(user),
          workShift: (user) => this.getWorkShiftName(user),
          entrydate: ({ entrydate }) => this.formatDate(entrydate),
          documentType: ({ documentType }) => typesDocuments[documentType] || typesDocuments["idcard"],
        };

        const data = Object.entries(dataToShow).reduce((acc, [key, valDic]) => {
          if (!user.hasOwnProperty(key)) {
            return acc;
          }
          const name = valDic;
          const value = responseValues.hasOwnProperty(key) ? responseValues[key](user) : user[key] && user[key] !== "null" ? user[key] : "";
          acc.push({ name, value });
          return acc;
        }, []);
        return data;
      },
      couldEditUser() {
        return ["superadmin", "coordinator"].includes(this.$userData.role);
      },
      inactiveReasonLabel() {
        if (this.check) return "";
        const history = this.user?.userStatusHistory;
        if (!history?.reason) return "";
        return this.optionsDisabled[history.reason] || history.reason;
      },
      currentStatus() {
        if (this.check) return "active";
        const eventType = this.user?.userStatusHistory?.eventType;
        if (eventType === "license") return "license";
        return "inactive";
      },
      statusMenuOptions() {
        const options = [];
        if (this.currentStatus === "active") {
          options.push({ name: "Inactivar", icon: "user_leave", action: () => this.onInactivateClick() });
          options.push({ name: "Crear licencia", icon: "umbrella_icon", action: () => this.onLicenseClick() });
        } else if (this.currentStatus === "license") {
          options.push({ name: "Activar", icon: "user_activate", action: () => this.onActivateClick() });
          options.push({ name: "Cancelar licencia", icon: "umbrella_icon", action: () => this.onCancelLicenseClick() });
        } else {
          options.push({ name: "Activar", icon: "user_activate", action: () => this.onActivateClick() });
          if (this.user?.userStatusHistory?.reason === "admin") {
            options.push({ name: "Crear licencia", icon: "umbrella_icon", action: () => this.onLicenseClick() });
            options.push({ name: "Cambiar motivo de inactivación", icon: "changeInactivation", action: () => this.onChangeReasonClick() });
          }
        }
        options.push({ name: "Ver historial", icon: "historyIcon", action: () => this.onHistoryClick() });
        return options;
      },
      optionsDisabled() {
        const optionsDisabled = {
          endContract: this.user.role === "model" ? "Finalización alianza" : "Termina contrato",
          waiver: "Renuncia",
          deserter: "Deserta",
          vacaciones: "Vacaciones",
          incapacidad: "Incapacidad",
          licencia: "Licencia",
          retornoLicencia: "Retorno de licencia",
          reconexionPorInactividad: "Reconexión por inactividad",
          admin: "Administrativo",
          adminOtro: "Otro motivo",
          adminGerencia: "Reunión con área",
          return: "Reconexión",
        };
        return optionsDisabled;
      },
    },
    watch: {
      user({ isEnable }) {
        this.check = isEnable;
      },
      "user.isEnable": {
        async handler() {
          try {
            const data = await userService.getUserData(this.user._id);
            this.setUserSelected(data);
          } catch (error) {
            console.log(error.message);
          }
        },
      },
      "$route.params.id": {
        handler() {
          this.expandedCardId = null;
        },
        immediate: true,
      },
    },
    methods: {
      ...mapMutations("user", { setUserSelected: "setUserSelected" }),
      async getSidebarData() {
        try {
          const data = await userStatusHistoryService.getUserStatusHistoryByUser(this.user._id);
          this.sidebarData = data;
        } catch (error) {
          console.error("Error al obtener los datos del sidebar:", error);
        }
      },
      toggleMenu() {
        this.showMenu = !this.showMenu;
      },
      handleClickOutside(event) {
        const menu = this.$refs.menuOptions;
        if (menu && !menu.contains(event.target)) {
          this.showMenu = false;
        }
      },
      onHistoryClick() {
        this.showMenu = false;
        this.openHistorySidebar();
      },
      onToggleStatusClick() {
        this.showMenu = false;
        this.changeCheck(!this.check);
      },
      onInactivateClick() {
        this.showMenu = false;
        this.showModalDisabled = true;
        this.showModalLicense = false;
        this.showModalChangeReason = false;
      },
      onActivateClick() {
        this.showMenu = false;
        this.changeCheck(true);
      },
      onLicenseClick() {
        this.showMenu = false;
        this.showModalLicense = true;
        this.showModalDisabled = false;
        this.showModalChangeReason = false;
        this.showModalChangeLicenseReason = false;
      },
      onChangeReasonClick() {
        this.showMenu = false;
        this.showModalChangeReason = true;
        this.showModalDisabled = false;
        this.showModalLicense = false;
        this.showModalChangeLicenseReason = false;
        this.showModalCancelLicense = false;
      },
      onCancelLicenseClick() {
        this.showMenu = false;
        this.showModalCancelLicense = true;
        this.showModalDisabled = false;
        this.showModalLicense = false;
        this.showModalChangeReason = false;
        this.showModalChangeLicenseReason = false;
      },
      openHistorySidebar() {
        if (!this.showSidebar) {
          this.getSidebarData();
          this.showSidebar = true;
        }
      },
      closeHistorySidebar() {
        this.showSidebar = false;
      },
      formatFullName(user) {
        const { name, lastname } = user;
        return `${name ?? ""} ${lastname ?? ""}`;
      },
      getWorkShiftName(user) {
        return user?.workShift?.name ? this.dicTurns[user.workShift.name] : "";
      },
      getIsEnableOrRasonDisabled(user) {
        const textIsEnable = user.isEnable ? "activo" : "";
        const textDisabled = user?.reasonDisable ? user.reasonDisable : "inactivo";
        return textIsEnable || textDisabled;
      },
      formatDate(date) {
        if (!date) return "";
        return this.$global.dateLocaleFormat(date, this.options);
      },
      openEditModal() {
        this.$store.dispatch("user/setInfoToEdit");

        this.$store.state.user.toShowInMagnament = 1;
      },
      closeModal() {
        this.showModalDisabled = false;
        this.showModalActive = false;
        this.showModalLicense = false;
        this.showModalChangeReason = false;
        this.showModalChangeLicenseReason = false;
        this.showModalCancelLicense = false;
        this.reset++;
      },
      async sendUserStatusHistory(data) {
        await userStatusHistoryService.sendUserStatusHistory(data);
      },
      async changeCheck(event) {
        if (!event) {
          this.showModalDisabled = true;
          this.showModalActive = false;
          return;
        } else if (this.user.role === "model") {
          this.showModalActive = true;
          this.showModalDisabled = false;
        } else {
          await this.sendUserStatusHistory({ userId: this.user._id, eventType: "enable", eventDate: new Date() });
          await this.setUpdateUser({ isEnable: event }, event);
        }
      },
      async setUpdateUser(body) {
        body._id = this.user._id;
        body.host = this.getAppByName("reservations").api || "";

        const { success, error, userEdited } = await this.$store.dispatch("user/updateUser", body);
        const msgSwal = {
          text: error ? error : "Usuario editado correctamente.",
          icon: success ? "success" : "error",
          confirmButtonColor: "#bd0909",
        };

        Swal.close();
        Swal.fire(msgSwal);

        if (success) {
          this.check = userEdited.isEnable;
          this.closeModal();
          try {
            const freshUser = await userService.getUserData(this.user._id);
            this.setUserSelected(freshUser);
          } catch (e) {
            console.error("Error refreshing user after update:", e);
          }
        }

        this.reset++;
      },
      async handleRefreshUser(freshUser) {
        if (!freshUser) return;
        try {
          this.setUserSelected(freshUser);
          this.closeModal();
        } catch (error) {
          console.error("Error handling refreshed user:", error);
        }
      },
      motiveDisable() {
        if (!this.showDisable) {
          this.showDisable = true;
          return;
        }
        if (this.showDisable) {
          this.update();
        }
        this.showDisable = false;
      },
    },
    beforeMount() {
      this.check = this.user.isEnable;
    },
    mounted() {
      document.addEventListener("click", this.handleClickOutside);
    },
    beforeDestroy() {
      document.removeEventListener("click", this.handleClickOutside);
    },
  };
</script>

<style lang="scss">
  .infoUser {
    height: fit-content;
    padding: $space-4;
    @include Flex(column, flex-start, flex-start);
    width: 100%;
    gap: $mpadding;
    &__wrapper {
      width: 100%;
      padding: $mpadding;
      background: #ffffff;
      box-shadow: 0px 1px 4px rgba(0, 0, 0, 0.25);
      border-radius: 10px;
    }
    &__containerStatus,
    &__containerStatusFuture {
      @include Flex(row, space-between);
      gap: 5px;
      padding: 5px;
      text-overflow: ellipsis;
      overflow: visible;
    }
    &__containerStatusFuture {
      flex-direction: column;
    }
    &__buttonsContainer {
      @include Flex(row, center, center);
      position: relative;
      gap: 8px;
    }
    &__statusBadge {
      padding: 3px 12px;
      border-radius: 21px;
      font-size: 14px;
      font-weight: 500;
      font-family: $newFont_regular;
      &--active {
        background: #f0fcf3;
        color: #2a9742;
        border: 1px solid rgba(42, 151, 66, 0.25);
      }
      &--inactive {
        background: rgba(189, 9, 9, 0.04);
        color: #bd0909;
        border: 1px solid #f2bdbd;
      }
    }
    &__reasonBadge {
      padding: 3px 12px;
      border-radius: 48px;
      border: 1px solid #e5e5e5;
      font-size: 14px;
      font-weight: 400;
      font-family: $newFont_regular;
      background: #f0f0f0;
      color: #3d3d3d;
    }
    &__iconsContainer {
      @include Flex(row);
      width: 30px;
      height: 30px;
      padding: 0;
      border: 0;
      border-radius: $radius-8;
      background: transparent;
      cursor: pointer;
      transition: all 0.4s ease;
      &:hover {
        background-color: #f5f5f5;
      }
      &--active {
        background-color: #f5f5f5;
      }
    }
    &__dots {
      font-size: 18px;
      color: #5b595c;
    }
    &__menuOptions {
      @include Flex(column, space-between, flex-start);
      position: absolute;
      width: 170px;
      gap: 4px;
      padding: $space-4;
      border-radius: $radius-8;
      background: #fff;
      right: 0;
      top: calc(100% + 4px);
      box-shadow: 0px 1px 6px 0px rgba(0, 0, 0, 0.25);
      z-index: 5;
    }
    &__optionIcon {
      font-size: 16px;
      color: #3d3d3d;
    }
    &__option {
      @include Flex(row, flex-start);
      gap: 8px;
      width: 100%;
      padding: $space-6;
      border: 0;
      background: transparent;
      font-family: $newFont_regular;
      color: #3d3d3d;
      font-weight: 400;
      text-align: left;
      cursor: pointer;
      border-radius: $radius-8;
      transition: all 0.4s ease;
      &:hover {
        background-color: #f5f5f5;
      }

      &--changeReason {
        align-items: flex-start;
        .infoUser__optionIcon {
          margin-top: 5px;
        }
      }
    }
    &__iconsContainer:focus-visible,
    &__option:focus-visible {
      outline: 2px solid $primary_color;
      outline-offset: 2px;
    }
    .slide-fade-enter-active,
    .slide-fade-leave-active {
      transition:
        transform 0.2s ease-out,
        opacity 0.1s ease-out;
    }
    .slide-fade-enter,
    .slide-fade-leave-to {
      transform: translateY(-8px);
      opacity: 0;
    }
    &__header {
      @include Flex(row, space-between);
    }
    &__headerStatus {
      @include Flex(column, center, flex-start);
    }
    &__title {
      font-family: $newFont_semiBold;
      font-size: 20px;
      font-weight: 600;
      color: #3d3d3d;
    }
    &__sesionContainer {
      @include Flex(row, flex-start);
      gap: $space-4;
      font-family: $newFont_regular;
      font-weight: 400;
      color: #595959;
      font-size: 13px;
    }
    &__iconEdit {
      padding: 5px;
      font-size: 20px;
      color: $chicago;
      cursor: pointer;
    }
    &__containerData {
      display: grid;
      grid-template-columns: 1fr;
    }
    &__containerInfo {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
      padding: 5px;
      text-overflow: ellipsis;
      overflow: auto;
      border-bottom: 1px solid $alto;
    }

    &__infoName {
      text-transform: capitalize;
      font-family: $newFont_medium;
      color: #9d9d9d;
    }
    &__infoValue {
      text-transform: capitalize;
      color: #495057;
      font-family: $newFont_regular;
    }
    &__contentTags {
      @include Flex(row, flex-start);
      flex-wrap: wrap;
      gap: 5px;
    }
    &__tag {
      padding: 2px 10px;
      text-transform: capitalize;
      font-size: 15px;
      border-radius: 14px;
      background: #f1f1f1;
      user-select: none;
    }
    .editUBtn {
      display: inline-flex;
    }
    .editUBtn * {
      pointer-events: none;
    }
    .editUBtn:active * {
      color: $primary_color;
    }
  }
</style>
